import { Button, Flex, Modal, TextInput } from "@mantine/core";
import { isEmail, isNotEmpty, useForm } from "@mantine/form";
import { useCallback } from "react";

import { BABABOS_TENANT_ID } from "../../../constants.ts";
import { CreateTenantInput } from "../../../graphql/generated";
import { Tenant } from "../../../types/tenant.ts";

interface Props {
  opened?: boolean;
  data: Tenant | null;
  onClose: () => void;
  onSubmit: (value: CreateTenantInput) => void;
}

const CreateTenantModal = ({
  opened = false,
  data,
  onClose,
  onSubmit,
}: Props) => {
  const form = useForm({
    validateInputOnBlur: true,
    initialValues: {
      name: data?.name ?? "",
      picName: data?.picName ?? "",
      email: data?.email ?? "",
      address: data?.address ?? "",
      phone: data?.phone ?? "",
    },
    validate: {
      email: isEmail("Invalid email"),
      name: isNotEmpty("Field is required"),
      picName: isNotEmpty("Field is required"),
      address: isNotEmpty("Field is required"),
      phone: isNotEmpty("Field is required"),
    },
  });

  const handleClose = useCallback(() => {
    onClose();
    form.reset();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    onSubmit(form.values as CreateTenantInput);
    handleClose();
  }, [form.values, handleClose, onSubmit]);

  return (
    <>
      <Modal
        opened={opened}
        title={data?.id ? "Update Tenant" : "Add Tenant"}
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        onClose={handleClose}
      >
        <Flex direction="column" gap={20}>
          <Flex gap={12} direction="column">
            <TextInput
              required
              disabled={data?.id === BABABOS_TENANT_ID}
              type="name"
              label="Tenant Name"
              placeholder="Enter Tenant Name"
              size="m"
              {...form.getInputProps("name")}
            />
            <TextInput
              required
              type="picName"
              label="Tenant PIC Name"
              placeholder="Enter Tenant PIC Name"
              size="m"
              {...form.getInputProps("picName")}
            />
            <TextInput
              required
              type="address"
              label="Tenant Address"
              placeholder="Enter Tenant Address"
              size="m"
              {...form.getInputProps("address")}
            />
            <TextInput
              required
              type="email"
              label="Tenant Email"
              placeholder="Enter Tenant Email"
              size="m"
              {...form.getInputProps("email")}
            />
            <TextInput
              required
              type="phone"
              label="Tenant Phone"
              placeholder="Enter Tenant Phone"
              size="m"
              {...form.getInputProps("phone")}
            />
          </Flex>
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={handleClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid()}
              onClick={handleSubmit}
            >
              {data?.id ? "save" : "add"}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default CreateTenantModal;
